import React from 'react';

const SearchAndPlaces = () => {
  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Search & Places</h1>
      
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
        <p className="text-sm text-blue-800">
          Find businesses using three different search methods: circles, pentagons, and tubes.
        </p>
      </div>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">Understanding Circle Radius</h2>
        <p className="text-gray-700 mb-3">
          The Google Places API returns a maximum of 20 places per search circle. Choosing the right radius is crucial to maximize your results without wasting API requests.
        </p>
        
        <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-4 flex flex-col items-start text-left">
          <p className="text-sm text-orange-800 mb-2">
            <strong>Key Principle:</strong> Use smaller circles in dense areas, larger circles in sparse areas.
          </p>
          <ul className="list-disc pl-5 space-y-1 text-sm text-orange-800">
            <li>Too small = underutilizing API requests (getting 5-10 places when you could get 20)</li>
            <li>Too large = missing potential places (the API only returns 20 out of hundreds available)</li>
          </ul>
        </div>

        <h3 className="text-lg font-semibold text-gray-900 mb-2">Recommended Radius by Area Type</h3>
        <div className="space-y-3">
          <div className="border-l-4 border-blue-500 bg-blue-50 p-3 flex flex-col items-start">
            <h4 className="font-semibold text-gray-900 mb-1">City Centers (e.g., Central London, Manhattan)</h4>
            <p className="text-sm text-gray-700"><strong>Radius:</strong> 200-500m</p>
            <p className="text-sm text-gray-600">High density areas need small circles to avoid missing businesses</p>
          </div>

          <div className="border-l-4 border-green-500 bg-green-50 p-3  flex flex-col items-start">
            <h4 className="font-semibold text-gray-900 mb-1">Suburban Areas (e.g., Greater London, Outer Boroughs)</h4>
            <p className="text-sm text-gray-700"><strong>Radius:</strong> 1000-1500m</p>
            <p className="text-sm text-gray-600">Medium density requires moderate circle sizes</p>
          </div>

          <div className="border-l-4 border-yellow-500 bg-yellow-50 p-3  flex flex-col items-start">
            <h4 className="font-semibold text-gray-900 mb-1">City Outskirts & Small Towns</h4>
            <p className="text-sm text-gray-700"><strong>Radius:</strong> 2000-3000m</p>
            <p className="text-sm text-gray-600">Lower density allows for larger coverage areas</p>
          </div>

          <div className="border-l-4 border-purple-500 bg-purple-50 p-3  flex flex-col items-start">
            <h4 className="font-semibold text-gray-900 mb-1">Rural Areas & Highways</h4>
            <p className="text-sm text-gray-700"><strong>Radius:</strong> 5000-10000m</p>
            <p className="text-sm text-gray-600">Very sparse areas need large circles to find businesses</p>
          </div>
        </div>

        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-3 mt-4">
          <p className="text-sm text-yellow-800 text-left">
            <strong>Tip:</strong> After searching, check the circle analysis button to see which circles hit the 20-place limit. This indicates you should use a smaller radius in those areas.
          </p>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">Search Methods</h2>
        
        <div className="space-y-4">
          <div className="border border-gray-200 rounded p-4 flex flex-col items-start text-left">
            <h3 className="font-semibold text-gray-900 mb-2">⭕ Circle Search</h3>
            <p className="text-gray-700 mb-2">Click anywhere on the map to add search circles.</p>
            <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600">
              <li>Adjust radius with the slider (500m - 50,000m)</li>
              <li>Add adjacent circles using directional buttons</li>
              <li>Select a circle to delete it or add surrounding areas</li>
            </ul>
          </div>

          <div className="border border-gray-200 rounded p-4 flex flex-col items-start text-left">
            <h3 className="font-semibold text-gray-900 mb-2">⬟ Pentagon Search</h3>
            <p className="text-gray-700 mb-2">Draw custom pentagon shapes for non-circular areas.</p>
            <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600">
              <li>Click map to place pentagon center</li>
              <li>Resize with shape size slider</li>
              <li>Press <kbd className="px-2 py-1 bg-gray-100 rounded text-xs">M</kbd> to toggle Move/Reshape modes</li>
              <li>Drag corners to reshape (in Reshape mode)</li>
              <li>Generate search circles inside the shape</li>
            </ul>
          </div>

          <div className="border border-gray-200 rounded p-4 flex flex-col items-start text-left">
            <h3 className="font-semibold text-gray-900 mb-2">⭘ Tube Search</h3>
            <p className="text-gray-700 mb-2">Ring-shaped areas, useful for searching around city perimeters.</p>
            <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600">
              <li>Click map to place tube center</li>
              <li>Adjust outer size and thickness</li>
              <li>Same reshape controls as pentagon</li>
              <li>Generate circles only in the tube area</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">Using Filters</h2>
        <p className="text-gray-700 mb-3">
          Control what types of businesses appear in your results.
        </p>
        
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="border border-gray-200 rounded p-3">
            <h4 className="font-medium text-green-700 mb-2">✓ Include Types</h4>
            <p className="text-sm text-gray-600">Businesses you want to find (e.g., restaurant, cafe, gym)</p>
          </div>
          <div className="border border-gray-200 rounded p-3">
            <h4 className="font-medium text-red-700 mb-2">✕ Exclude Types</h4>
            <p className="text-sm text-gray-600">Businesses to filter out (e.g., fast_food, chain stores)</p>
          </div>
        </div>

        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-3">
          <p className="text-sm text-yellow-800">
            <strong>Tip:</strong> You must specify at least one included type before searching.
          </p>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">Search Results</h2>
        <p className="text-gray-700 mb-3 text-left">
          After searching, results appear at the bottom of the page.
        </p>
        <ul className="list-disc pl-5 space-y-2 text-gray-700  flex flex-col items-start">
          <li><strong>Review results:</strong> Check business names, addresses, phone numbers, and websites</li>
          <li><strong>Remove unwanted:</strong> Click the x on any place to remove it from results</li>
          <li><strong>Chain detection:</strong> If chains are found, click the chains button to bulk remove them</li>
          <li><strong>Save places:</strong> Click "Save Places" to add them to your collection</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">Chain Detection</h2>
        <p className="text-gray-700 mb-3 text-left">
          The system automatically detects franchise chains and large businesses.
        </p>
        <ol className="list-decimal pl-5 space-y-2 text-gray-700  flex flex-col items-start">
          <li>After search completes, check if chains were detected</li>
          <li>Click the "🔗 chains" button to open the chain modal</li>
          <li>Select chains you want to remove</li>
          <li>Check "Keep removing for future searches" to auto-exclude them going forward</li>
          <li>Click "Remove & Exclude" to clean up results</li>
        </ol>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">Saved Places</h2>
        <p className="text-gray-700 mb-3 text-left">
          Access your saved places from the "Saved Places" button in the top navigation.
        </p>
        <ul className="list-disc pl-5 space-y-2 text-gray-700  flex flex-col items-start">
          <li><strong>View all saved businesses</strong> in a searchable table</li>
          <li><strong>Export to CSV</strong> for use in other tools</li>
          <li><strong>Convert to leads</strong> for further enrichment with crawling</li>
          <li><strong>Delete places</strong> individually or in bulk</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">Tips & Best Practices</h2>
        <div className="bg-gray-50 border border-gray-200 rounded p-4 space-y-3">
          <div>
            <h4 className="font-medium text-gray-900 mb-1">Use city navigation</h4>
            <p className="text-sm text-gray-600">Click city buttons at the top to quickly jump to major cities</p>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-1">Watch circle density alerts</h4>
            <p className="text-sm text-gray-600">If circles hit the 20-place limit, reduce radius for better coverage</p>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-1">Keyboard shortcuts</h4>
            <p className="text-sm text-gray-600">
              Press <kbd className="px-2 py-1 bg-gray-100 rounded text-xs">M</kbd> to toggle shape modes, 
              <kbd className="px-2 py-1 bg-gray-100 rounded text-xs ml-1">Ctrl+Z</kbd> to undo shape changes
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SearchAndPlaces;